import React, { useState, useEffect, useRef } from 'react';
import { storage } from '../firebase/config';
import { ref, getDownloadURL } from 'firebase/storage';
import { db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import DisplayTitleEditorModal from './DisplayTitleEditorModal';
import './AlbumDetail.css';

// Helper function to normalize text: remove accents and convert to Title Case
function normalizeText(text) {
  if (!text) return text;
  // Remove accents
  const normalized = text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  // Convert to Title Case
  return normalized
    .split(' ')
    .map(word => {
      if (!word) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

// Helper function to render DisplayTitle with artist part in blue
function renderDisplayTitle(displayTitle) {
  if (!displayTitle) return displayTitle;
  
  // Normalize the DisplayTitle text
  const normalized = normalizeText(displayTitle);
  
  // Check if it contains " - " (artist separator)
  const parts = normalized.split(' - ');
  if (parts.length >= 2) {
    // Has artist part - render title in white and artist in blue
    const titlePart = parts[0];
    const artistPart = parts.slice(1).join(' - '); // In case there are multiple dashes
    
    return (
      <>
        {titlePart}
        {' - '}
        <span className="tracklist-artist">{artistPart}</span>
      </>
    );
  }
  
  // No artist part, just return normalized text
  return normalized;
}

function AlbumDetail({ album, currentTrack, isPlaying, onPlayTrack, onPlayAlbum, onAddToQueue, onClose, onCollapse, isCollapsed, onUpdateMetadata, onAlbumUpdate, isInScannedMode }) {
  const [coverImageUrl, setCoverImageUrl] = useState(null);
  const [loadingImage, setLoadingImage] = useState(false);
  const [focusedSongIndex, setFocusedSongIndex] = useState(-1);
  const [selectedTrackForEdit, setSelectedTrackForEdit] = useState(null);
  const [showDisplayTitleEditor, setShowDisplayTitleEditor] = useState(false);
  const [normalizedAlbum, setNormalizedAlbum] = useState(null);
  const activeTrackRef = useRef(null);
  const tracklistRef = useRef(null);

  // Normalize tracks when album is loaded
  useEffect(() => {
    if (!album) {
      setNormalizedAlbum(null);
      return;
    }

    // Normalize the album's songs array
    const normalizeTracks = (songs) => {
      if (!Array.isArray(songs)) {
        return [];
      }

      return songs
        .filter(song => song !== null && song !== undefined) // Remove null/undefined entries
        .map((song, index) => {
          // Ensure each track has required fields
          return {
            ...song,
            Id: song.Id || song.id || index,
            Title: song.Title || song.title || `Track ${index + 1}`,
            DisplayTitle: song.DisplayTitle || song.Title || song.title || `Track ${index + 1}`,
            trackNumber: song.trackNumber || index + 1,
          };
        });
    };

    const normalizedSongs = normalizeTracks(album.songs);
    setNormalizedAlbum({
      ...album,
      songs: normalizedSongs,
    });
  }, [album]);

  // Use normalized album if available, otherwise use original
  const displayAlbum = normalizedAlbum || album;

  // Auto-scroll to the currently playing track
  useEffect(() => {
    if (activeTrackRef.current) {
      activeTrackRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [currentTrack]);

      // Gamepad queue/song navigation
  useEffect(() => {
    const handleQueueNavigate = (e) => {
      const { direction } = e.detail;
      if (!displayAlbum?.songs || !Array.isArray(displayAlbum.songs) || displayAlbum.songs.length === 0) return;
      
      const songItems = tracklistRef.current?.querySelectorAll('.tracklist-item');
      if (!songItems || songItems.length === 0) return;
      
      // Remove focus from all
      songItems.forEach(item => item.classList.remove('gamepad-focused'));
      
      let newIndex = focusedSongIndex;
      
      if (direction === 'up') {
        newIndex = newIndex > 0 ? newIndex - 1 : songItems.length - 1;
      } else if (direction === 'down') {
        newIndex = newIndex < songItems.length - 1 ? newIndex + 1 : 0;
      }
      
      // Default to first song if none focused
      if (newIndex < 0) newIndex = 0;
      
      // Apply focus
      if (songItems[newIndex]) {
        songItems[newIndex].classList.add('gamepad-focused');
        songItems[newIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        setFocusedSongIndex(newIndex);
        console.log('🎮 Song focus:', newIndex, displayAlbum.songs[newIndex]?.Title);
      }
    };
    
    const handleQueueSelect = () => {
      if (focusedSongIndex >= 0 && displayAlbum?.songs?.[focusedSongIndex]) {
        console.log('🎮 Song select:', displayAlbum.songs[focusedSongIndex].Title);
        onPlayTrack(displayAlbum.songs[focusedSongIndex], displayAlbum);
      }
    };
    
    window.addEventListener('gamepadQueueNavigate', handleQueueNavigate);
    window.addEventListener('gamepadSelect', handleQueueSelect);
    
    return () => {
      window.removeEventListener('gamepadQueueNavigate', handleQueueNavigate);
      window.removeEventListener('gamepadSelect', handleQueueSelect);
    };
  }, [focusedSongIndex, displayAlbum, onPlayTrack]);

  useEffect(() => {
    let isMounted = true; // Track if component is still mounted

      const loadCoverImage = async () => {
      if (!displayAlbum?.coverUrl) {
        if (isMounted) setCoverImageUrl(null);
        return;
      }

      // Skip loading if album is marked as unavailable
      if (displayAlbum.storageStatus === 'unavailable') {
        if (isMounted) {
          setCoverImageUrl(null);
          setLoadingImage(false);
        }
        return;
      }

      if (isMounted) setLoadingImage(true);
      
      try {
        let storagePath = displayAlbum.coverUrl;
        if (storagePath.startsWith('/')) {
          storagePath = storagePath.substring(1);
        }
        
        // Prepend the type (Albums or Singles) if not already in the path
        const type = displayAlbum.type || 'Albums';
        if (!storagePath.startsWith(type + '/')) {
          storagePath = `${type}/${storagePath}`;
        }
        
        // For Singles, path structure is ALWAYS: Singles/Language/Various/Variety/Mix/Artist/album.jpg
        // Genre/Subgenre are hardcoded as Various/Variety/Mix regardless of database values
        // The coverUrl stored in Firestore should already have the full correct path
        
        const imageRef = ref(storage, storagePath);
        const url = await getDownloadURL(imageRef);
        
        // Only update state if component is still mounted
        if (isMounted) {
          setCoverImageUrl(url);
        }
      } catch (error) {
        // Silently fail - don't log permission errors for unavailable files
        if (isMounted) {
          setCoverImageUrl(null);
        }
      } finally {
        if (isMounted) {
          setLoadingImage(false);
        }
      }
    };

    loadCoverImage();

    // Cleanup function to prevent memory leaks
    return () => {
      isMounted = false;
    };
  }, [displayAlbum?.coverUrl, displayAlbum?.storageStatus, displayAlbum?.type]);

  if (!album && !normalizedAlbum) {
    return (
      <div className="album-detail-empty">
        <div className="album-detail-empty-content">
          <span className="album-detail-empty-icon">🎵</span>
          <p>Select an album to view details</p>
        </div>
        {onClose && (
          <div className="album-detail-done-button-container">
            <button 
              className="album-detail-done-button"
              onClick={onClose}
            >
              ✓ Done
            </button>
          </div>
        )}
      </div>
    );
  }

  const totalSongs = Array.isArray(displayAlbum?.songs) ? displayAlbum.songs.length : 0;
  const totalDuration = Array.isArray(displayAlbum?.songs) ? displayAlbum.songs.reduce((acc, song) => {
    // TODO: Calculate actual duration when available
    return acc + 0;
  }, 0) : 0;

  return (
    <div className="album-detail">
      {/* Desktop Collapse Button */}
      {onCollapse && (
        <div className="album-detail-collapse-container">
          <button 
            className="album-detail-collapse-btn"
            onClick={onCollapse}
            title="Hide Now Playing panel"
          >
            ✕ Hide Panel
          </button>
        </div>
      )}

      <div className="album-detail-scrollable-content">
      {/* Header Row: Cover + Buttons side by side */}
      <div className="album-detail-header-row">
        {/* Album Cover */}
        <div className="album-detail-cover">
          {loadingImage ? (
            <div className="album-detail-cover-placeholder">
              <span>⏳</span>
            </div>
          ) : coverImageUrl ? (
            <img src={coverImageUrl} alt={displayAlbum?.album} />
          ) : (
            <div className="album-detail-cover-placeholder">
              <span>🎵</span>
            </div>
          )}
        </div>

        {/* Action Buttons - Stacked next to cover */}
        <div className="album-detail-actions">
          <button 
            className="btn btn-primary btn-play-all"
            onClick={() => {
              if (displayAlbum?.storageStatus !== 'unavailable') {
                onPlayAlbum(displayAlbum, false);
              }
            }}
            disabled={displayAlbum?.storageStatus === 'unavailable'}
            style={displayAlbum?.storageStatus === 'unavailable' ? { 
              opacity: 0.5, 
              cursor: 'not-allowed',
              background: '#555'
            } : {}}
            title={displayAlbum?.storageStatus === 'unavailable' ? 'Files not available' : 'Play album in order'}
          >
            ▶ Play
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => {
              if (displayAlbum?.storageStatus !== 'unavailable') {
                onPlayAlbum(displayAlbum, true);
              }
            }}
            disabled={displayAlbum?.storageStatus === 'unavailable'}
            style={displayAlbum?.storageStatus === 'unavailable' ? { 
              opacity: 0.5, 
              cursor: 'not-allowed',
              background: '#555'
            } : {}}
            title={displayAlbum?.storageStatus === 'unavailable' ? 'Files not available' : 'Shuffle and play album'}
          >
            🔀 Shuffle
          </button>
          <button 
            className="btn btn-secondary"
            onClick={(e) => {
              e.stopPropagation();
              console.log('🛠️ Update Metadata button clicked', { album: displayAlbum, onUpdateMetadata: !!onUpdateMetadata });
              if (onUpdateMetadata) {
                onUpdateMetadata(displayAlbum);
              } else {
                console.warn('⚠️ onUpdateMetadata handler not provided');
                alert('Metadata update feature not available. Please navigate to Dashboard to update metadata.');
              }
            }}
            style={{
              background: '#667eea',
              color: 'white',
              border: 'none'
            }}
            title="Update metadata from MusicBrainz"
          >
            🛠️ Update Metadata
          </button>
        </div>
      </div>

      {/* Album Info - Single line */}
      <div className="album-detail-info">
        <p className="album-detail-title">
          {displayAlbum?.album || 'Unknown Album'} - <span className="album-detail-artist">{displayAlbum?.artist || 'Unknown Artist'}</span> • {totalSongs} song{totalSongs !== 1 ? 's' : ''}
        </p>

        {/* Warning for unavailable albums */}
        {displayAlbum?.storageStatus === 'unavailable' && (
          <div style={{
            marginTop: '0.5rem',
            padding: '0.5rem',
            background: 'rgba(255, 152, 0, 0.15)',
            border: '1px solid rgba(255, 152, 0, 0.4)',
            borderRadius: '6px',
            color: '#ffb74d',
            fontSize: '0.75rem'
          }}>
            <strong>⚠️ Files Not Available</strong>
          </div>
        )}
      </div>

      {/* Tracklist */}
      <div className="album-detail-tracklist">
        <h3>Tracklist</h3>
        <div className="tracklist-items" ref={tracklistRef}>
          {Array.isArray(displayAlbum?.songs) && displayAlbum.songs.length > 0 ? (
            displayAlbum.songs.map((song, index) => {
              const isCurrentTrack = currentTrack?.Title === song.Title;
              const isCurrentlyPlaying = isCurrentTrack && isPlaying;
              
              return (
                <div 
                  key={song.Id || index}
                  ref={isCurrentTrack ? activeTrackRef : null}
                  className={`tracklist-item ${isCurrentTrack ? 'active' : ''}`}
                  onClick={(e) => {
                    console.log('🖱️ Track clicked:', {
                      trackTitle: song?.Title,
                      albumStorageStatus: displayAlbum?.storageStatus,
                      isCtrlClick: e.ctrlKey || e.metaKey,
                      spotifyUri: song?.spotifyUri,
                      spotifyId: song?.spotifyId,
                      isInScannedMode: isInScannedMode,
                    });
                    
                    // Control+click (or Cmd+click on Mac) opens editor
                    if (e.ctrlKey || e.metaKey) {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('✏️ Opening track editor (Ctrl/Cmd+click)');
                      setSelectedTrackForEdit(song);
                      setShowDisplayTitleEditor(true);
                    } else if (displayAlbum?.storageStatus !== 'unavailable') {
                      console.log('▶️ Calling onPlayTrack for:', song?.Title);
                      onPlayTrack(song, displayAlbum);
                    } else {
                      console.warn('⛔ Track click blocked - album storageStatus is unavailable');
                    }
                  }}
                  onContextMenu={(e) => {
                    // Right-click also opens editor
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedTrackForEdit(song);
                    setShowDisplayTitleEditor(true);
                  }}
                  style={displayAlbum?.storageStatus === 'unavailable' ? { 
                    opacity: 0.6, 
                    cursor: 'not-allowed' 
                  } : {}}
                >
                  <div className="tracklist-number">
                    {isCurrentlyPlaying ? (
                      <span className="playing-icon">♫</span>
                    ) : (
                      <span>{index + 1}</span>
                    )}
                  </div>
                  <div className="tracklist-info">
                    <p className="tracklist-title">
                      {renderDisplayTitle(song.DisplayTitle || song.Title || `Track ${index + 1}`)}
                    </p>
                    {song.Duration && song.Duration !== 'null' && (
                      <p className="tracklist-duration">{song.Duration}</p>
                    )}
                  </div>
                  <button 
                    className="tracklist-play-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (displayAlbum?.storageStatus !== 'unavailable') {
                        onPlayTrack(song, displayAlbum);
                      }
                    }}
                    disabled={displayAlbum?.storageStatus === 'unavailable'}
                    style={displayAlbum?.storageStatus === 'unavailable' ? { 
                      opacity: 0.5, 
                      cursor: 'not-allowed' 
                    } : {}}
                    title={displayAlbum?.storageStatus === 'unavailable' ? 'File not available' : ''}
                  >
                    {isCurrentlyPlaying ? '⏸' : '▶'}
                  </button>
                </div>
              );
            })
          ) : (
            <p className="tracklist-empty">No tracks available</p>
          )}
        </div>
      </div>
      </div>

      {/* Done Button - Only visible on mobile */}
      {onClose && (
        <div className="album-detail-done-button-container">
          <button 
            className="album-detail-done-button"
            onClick={onClose}
          >
            ✓ Done
          </button>
        </div>
      )}

      {/* DisplayTitle Editor Modal */}
      <DisplayTitleEditorModal
        isOpen={showDisplayTitleEditor}
        onClose={() => {
          setShowDisplayTitleEditor(false);
          setSelectedTrackForEdit(null);
        }}
        track={selectedTrackForEdit}
        album={displayAlbum}
        onSave={async (updatedTrack) => {
          // The modal handles saving to Firestore via API
          // This callback is called after successful save
          console.log('Track updated successfully:', updatedTrack);
          console.log('Current album before reload:', { id: displayAlbum?.id, collection: displayAlbum?.collection, songsCount: displayAlbum?.songs?.length });
          
          // Reload the album from Firestore to get the updated data
          if (displayAlbum?.id) {
            try {
              // Try albums-flat first, then JBXX
              const collections = [displayAlbum.collection || 'albums-flat', 'albums-flat', 'JBXX'];
              let updatedAlbumData = null;
              
              for (const collection of collections) {
                try {
                  const albumDoc = await getDoc(doc(db, collection, displayAlbum.id));
                  if (albumDoc.exists()) {
                    const data = albumDoc.data();
                    updatedAlbumData = { id: albumDoc.id, collection, ...data };
                    console.log(`✅ Reloaded album from Firestore (${collection}):`, {
                      id: updatedAlbumData.id,
                      songsCount: updatedAlbumData.songs?.length,
                      hasSongs: !!updatedAlbumData.songs,
                      isArray: Array.isArray(updatedAlbumData.songs)
                    });
                    
                    if (updatedAlbumData.songs && Array.isArray(updatedAlbumData.songs) && updatedAlbumData.songs.length > 0) {
                      break; // Found valid album with songs
                    }
                  }
                } catch (err) {
                  console.warn(`Error loading from ${collection}:`, err.message);
                }
              }
              
              if (updatedAlbumData) {
                // Update the album via the parent component if callback is provided
                if (onAlbumUpdate) {
                  onAlbumUpdate(updatedAlbumData);
                } else {
                  console.warn('⚠️ onAlbumUpdate callback not provided');
                }
              } else {
                console.error('❌ Could not reload album from any collection');
              }
            } catch (error) {
              console.error('Error reloading album:', error);
            }
          } else {
            console.warn('⚠️ Cannot reload album - missing id:', displayAlbum);
          }
        }}
      />
    </div>
  );
}

export default AlbumDetail;


